import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import AbbrTip from '@/components/AbbrTip';

const FAQS = [
  {
    q: 'Who was Benjamin Graham?',
    a: 'Born in London in 1894 and raised in New York, Graham watched his family lose everything in the Panic of 1907. He went to Columbia, then to Wall Street, founded the Graham-Newman Partnership, lived through the 1929 crash and wrote the two books that defined value investing: Security Analysis (1934, with David Dodd) and The Intelligent Investor (1949). Warren Buffett, Walter Schloss and Mario Gabelli were all his students or disciples.',
  },
  {
    q: 'What is a margin of safety?',
    a: 'Graham\u2019s central idea: buy a business for meaningfully less than a conservative estimate of what it is worth, so that errors in your analysis, bad luck or bad management are absorbed by the discount instead of by your capital. It is a cushion, not a forecast.',
  },
  {
    q: 'What is a net-net stock?',
    a: 'A company trading below its net current asset value: current assets minus all liabilities and preferred stock, with factories, land and equipment counted at zero. Graham\u2019s buy trigger was a price at or below two-thirds of that figure \u2014 buying a dollar of liquid assets for about 67 cents. DiviDrip shows NCAV per share on the Mega Tier table and flags any stock that meets the two-thirds rule.',
  },
  {
    q: 'Who is Joseph Piotroski?',
    a: 'An accounting professor (MBA Indiana, PhD Michigan) who, while teaching at the University of Chicago in 2000, published \u201cValue Investing: The Use of Historical Financial Statement Information to Separate Winners from Losers.\u201d He showed that although cheap high book-to-market stocks beat the market as a group, more than half of them individually lost money or went bankrupt \u2014 the group return came from a few big winners.',
  },
  {
    q: 'What are the nine F-Score tests?',
    a: 'Profitability: return on assets above zero; operating cash flow above zero; ROA higher than last year; operating cash flow greater than net income. Leverage and liquidity: long-term debt to assets fell; current ratio rose; no new shares issued. Efficiency: gross margin rose; asset turnover rose. One point each, scored against the prior fiscal year.',
  },
  {
    q: 'What is a good F-Score?',
    a: '8 or 9 marks a company whose operations are visibly strengthening. 5 to 7 is a stable baseline. 0 to 4 is the danger zone where most value traps live. DiviDrip\u2019s Mega Tier funnel requires 7 or better.',
  },
  {
    q: 'Why run Graham before Piotroski?',
    a: 'Graham\u2019s ratios are static and cheap to compute, and they remove 85\u201390% of the market at once. Running the F-Score first would hand you a long list of superb but expensive businesses that never pass a value test. Piotroski built his score for stocks that are already cheap and unloved; applied there, a high score marks the beaten-down company that is quietly improving.',
  },
  {
    q: 'Does this work for non-dividend stocks?',
    a: 'Yes. Neither framework needs a dividend. Graham preferred payers as evidence of real earnings, but his net-net method ignores dividends entirely. Piotroski\u2019s score is dividend-neutral \u2014 in fact one test rewards not raising cash from shareholders. DiviDrip\u2019s Mega Tier page runs both on dividend and non-dividend stocks alike.',
  },
];

const FSCORE = [
  ['Profitability', 'Return on assets > 0', 'The business earned money on what it owns'],
  ['Profitability', 'Operating cash flow > 0', 'Cash actually came in the door'],
  ['Profitability', 'ROA higher than last year', 'Earning power is improving, not fading'],
  ['Profitability', 'Cash flow > net income', 'Profits are cash, not accruals'],
  ['Leverage & liquidity', 'Long-term debt ÷ assets fell', 'Deleveraging, not borrowing to survive'],
  ['Leverage & liquidity', 'Current ratio rose', 'Short-term liquidity improved'],
  ['Leverage & liquidity', 'No new shares issued', 'Not funding losses by diluting you'],
  ['Efficiency', 'Gross margin rose', 'Pricing power or cost control'],
  ['Efficiency', 'Asset turnover rose', 'More revenue per dollar of assets'],
];

const GRAHAM = [
  ['P/E ≤ 15', 'No more than 15 years of current earnings for the price'],
  ['P/B ≤ 1.5, or P/E × P/B ≤ 22.5', 'Price anchored to book value; the product rule is the Graham Number in disguise'],
  ['Current ratio ≥ 1.5', 'Can pay the bills for the next year without borrowing (Graham asked for 2)'],
  ['Long-term debt ≤ working capital', 'Debt could be retired from liquid assets alone'],
  ['Positive earnings, this year and last', 'Not a one-year accounting fluke'],
];

export default function LearnGrahamPiotroskiDeepValue() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };

  return (
    <LearnPage
      title="Graham + Piotroski — Deep Value Without the Traps | TwylightCrow"
      description="Benjamin Graham's margin of safety and net-net method, Joseph Piotroski's nine-test F-Score, why Graham runs first and Piotroski last, and how DiviDrip's Mega Tier funnel applies both to dividend and non-dividend stocks."
      canonical="https://twylightcrow.com/learn/graham-piotroski-deep-value"
      guidePath="/learn/graham-piotroski-deep-value"
      sectionLabel="Value Frameworks"
      sectionAnchor="value-frameworks"
      kicker="Learn · Dividend & Non-Dividend"
      h1="Graham + Piotroski — Deep Value Without the Traps"
      datePublished="2026-09-12"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/hidden-gems-vs-value-traps', label: 'Hidden Gems vs Value Traps — the qualitative side of the same question' },
        { to: '/learn/beneish-altman-forensic-safety', label: 'Beneish & Altman — the forensic check to run on anything that passes' },
        { to: '/learn/special-dividends-in-cagr-math', label: 'Special Dividends and CAGR Math — why a one-off payout is not a yield' },
        { to: '/learn/payout-ratio', label: 'Payout Ratio — the dividend-side cousin of the earnings-quality test' },
      ]}
    >
      <p>
        Two men, seventy years apart, solved the two halves of the same problem. Benjamin
        Graham worked out how to buy a business for less than it is worth. Joseph Piotroski
        worked out how to tell which cheap businesses were cheap for a reason. Put the two
        together and you have a defensive framework that finds severely mispriced companies
        with the operating strength to actually recover &mdash; and throws out the value
        traps that make most bargain-hunting so expensive.
      </p>
      <p>
        This guide covers both men, both methods, the order they should run in, and exactly
        how DiviDrip&rsquo;s <Link to="/mega-tier" className="ht-inline-link" data-testid="guide-mega-tier-link">Mega Tier Undervalued Stocks</Link> funnel applies them
        to dividend and non-dividend stocks alike.
      </p>

      <h2>The architect of value: Benjamin Graham</h2>
      <p>
        Graham was born in London in 1894 and moved to New York as a child. His family was
        ruined in the Panic of 1907, and that early loss shaped a lifelong obsession with
        the preservation of capital. After Columbia he went to Wall Street and founded the
        Graham-Newman Partnership; the 1929 crash then gave him a front-row seat to what
        happens when prices detach completely from businesses. His response was to write
        the framework the market lacked: <em>Security Analysis</em> (1934, with David Dodd)
        and <em>The Intelligent Investor</em> (1949), which Warren Buffett called
        &ldquo;by far the best book about investing ever written.&rdquo;
      </p>
      <p>
        Graham&rsquo;s method strips emotion out of the equation. A stock is a fractional
        ownership interest in a real business, not a lottery ticket. Prices are set by
        &ldquo;Mr. Market&rdquo; &mdash; his allegory for a manic-depressive business partner
        who quotes wild numbers every day &mdash; and the investor&rsquo;s job is to take
        the bargains and ignore the rest. Buying at a steep discount to a conservative
        estimate of value creates a <AbbrTip term="Margin of Safety">margin of safety</AbbrTip>: a cushion that absorbs
        analytical mistakes, management errors and bad luck before they reach your capital.
      </p>

      <h2>The deep-value blueprint</h2>
      <p>
        Graham&rsquo;s most extreme tool is the <strong>net-net</strong>. Take current
        assets, subtract every liability and any preferred stock, and you have
        <strong> net current asset value</strong> (<AbbrTip term="NCAV">NCAV</AbbrTip>). Factories, land and machinery are
        valued at zero. If the whole company can be bought for two-thirds of that liquid
        figure or less, you are paying roughly 67 cents for a dollar of cash, receivables and
        inventory &mdash; and getting the operating business for free.
      </p>
      <table className="learn-table">
        <thead><tr><th>Graham check used on DiviDrip</th><th>What it protects against</th></tr></thead>
        <tbody>
          {GRAHAM.map(([rule, why]) => (
            <tr key={rule}><td className="font-mono">{rule}</td><td>{why}</td></tr>
          ))}
        </tbody>
      </table>
      <p>
        True net-nets are rare in a modern market, so DiviDrip treats the two-thirds rule
        as a badge rather than a gate: the Mega Tier table shows NCAV per share for every
        ranked stock and flags <strong>NET-NET</strong> when the price is at or below
        two-thirds of it. The five checks in the table above are the working filter, and a
        stock must pass at least four.
      </p>

      <h2>The modern quant: Joseph Piotroski</h2>
      <p>
        By the turn of the millennium the market had a problem Graham never quantified.
        Cheap, unloved stocks still beat the market as a group &mdash; but Piotroski, an
        accounting professor at the University of Chicago, found that more than half of
        them individually lost money or went bankrupt. The group return came from a
        handful of big winners dragging up a pile of &ldquo;value traps.&rdquo;
      </p>
      <p>
        His 2000 paper, <em>Value Investing: The Use of Historical Financial Statement
        Information to Separate Winners from Losers</em>, proposed a fix that needed nothing
        but the last two annual reports: nine binary accounting tests, one point each,
        scored against the prior year. The result &mdash; the <AbbrTip term="F-Score">F-Score</AbbrTip> &mdash;
        measures whether a company&rsquo;s operating trajectory is improving or decaying.
      </p>
      <table className="learn-table">
        <thead><tr><th>Pillar</th><th>Test (1 point if true)</th><th>What it means</th></tr></thead>
        <tbody>
          {FSCORE.map(([pillar, test, why]) => (
            <tr key={test}><td className="font-semibold">{pillar}</td><td>{test}</td><td>{why}</td></tr>
          ))}
        </tbody>
      </table>
      <p>
        <strong>8&ndash;9</strong> marks a financial powerhouse or a genuine turnaround.
        <strong> 5&ndash;7</strong> is a stable baseline. <strong>0&ndash;4</strong> is the
        danger zone where the traps live. Note what is <em>not</em> in the list: no price,
        no dividend, no growth forecast. The score is dividend-neutral &mdash; one test even
        rewards a company for not raising cash from shareholders &mdash; which is why it
        works just as well on a cash-hoarding software company as on an industrial payer.
      </p>

      <h2>Why Graham runs first and Piotroski runs last</h2>
      <p>
        Order matters. Graham&rsquo;s ratios are static and cheap to compute, and they
        eliminate 85&ndash;90% of the market in one pass &mdash; every growth stock,
        every fairly priced blue chip. Run the F-Score first instead and you get a long
        list of superb, expensive businesses that will never pass a value check; Piotroski
        designed his score for stocks that are <em>already</em> cheap, and its
        outperformance fades when it is applied to high-fliers.
      </p>
      <p>
        Applied second, the F-Score asks the right question of the right list: of the
        companies the market is pricing for death, which ones are quietly improving under
        the hood? A Graham-cheap stock with an F-Score of 8 or 9 is a bargain caught at the
        moment its operations turn.
      </p>

      <h2>How DiviDrip runs it: the Mega Tier funnel</h2>
      <ol>
        <li>
          <strong>Rank on quality and price.</strong> Every operating company with statements
          on file is ranked on return on capital (EBIT &divide; net fixed assets plus working
          capital) and on earnings yield (EBIT &divide; enterprise value). The two ranks are
          added and the best 20% move on. Banks, insurers, utilities, REITs, BDCs and every
          fund wrapper are excluded because the EBIT math does not describe them.
        </li>
        <li><strong>Graham.</strong> At least four of the five deep-value checks above.</li>
        <li><strong>Piotroski.</strong> An F-Score of 7 or better against the prior fiscal year.</li>
      </ol>
      <p>
        The list is recomputed weekly from annual filings. It is deliberately short: on a
        normal week a few thousand stocks become a handful. Each stage&rsquo;s survivors are
        one click away on the page, so you can see exactly where a name fell out.
      </p>

      <h2>What to do with a name that passes</h2>
      <ol>
        <li>Ask why it is cheap. A 50% drawdown is usually the reason a stock passes Graham; the F-Score says the business is fine, the chart says investors disagree. Find the story.</li>
        <li>Check the payout label. A single special dividend can masquerade as a 40% yield in raw data. DiviDrip classes special-only payers as non-dividend stocks so the yield never lies to you.</li>
        <li>Run the forensic panel. <Link to="/learn/beneish-altman-forensic-safety" className="ht-inline-link">Beneish and Altman</Link> catch accounting manipulation and distress that neither framework tests directly.</li>
        <li>Size for patience. Graham held net-nets for two to three years or a 50% gain, whichever came first. Deep value is slow.</li>
      </ol>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }, i) => (
          <div key={i} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>
    </LearnPage>
  );
}
